import React from 'react';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import WartelistePng from './images/warteliste.png';
import WartelisteWebp from './images/warteliste.webp';
import WartelisteMobilePng from './images/warteliste-mobile.png';
import WartelisteMobileWebp from './images/warteliste-mobile.webp';

import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { ProductType } from '../../../../../@types/product';
import { report } from '../../../../../tracking/tracking';
import { OverviewTrackingEvents } from '../../../../../tracking/trackingEvents';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';
import { useGetHasProduct } from '@/customHooks/useGetHasProduct.hook';
import { APP_CONFIG } from '@/config';

const styles = {
  overviewTab: { maxWidth: 530 },
};

const OverviewTab = ({ isEntitledPlusRent }: { isEntitledPlusRent: boolean }) => {
  return (
    <div style={styles.overviewTab}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg
          id='benefits.warteliste.modal.overview.paragraph1'
          defaultMessage='Mit Wartelisten können Vermieter:innen dein Interesse sehen und dich schon vor der Veröffentlichung eines Inserats kontaktieren.'
        />
      </div>
      <div className='padding-bottom-l'>
        <Msg
          id='benefits.warteliste.modal.overview.paragraph2'
          defaultMessage='Die Funktion nutzt deine gespeicherte Suche und bezieht derzeit deaktivierte Inserate ein. Wird ein Objekt wieder verfügbar, können Vermieter:innen dich direkt anschreiben – ohne ein neues Inserat erstellen zu müssen.'
        />
      </div>
      {!isEntitledPlusRent && (
        <ul className='list-check list-spacing-s'>
          <li data-testid='mieter-plus-label-with-checkmark'>
            <Msg
              id='benefits.warteliste.modal.activeMieterPlus'
              defaultMessage='In Suchen+ für Miete enthalten'
              values={{
                Logo: <PlusBadge variant='search' />,
              }}
            />
          </li>
        </ul>
      )}
    </div>
  );
};

const ButtonArea = ({ isEntitledPlusRent }: { isEntitledPlusRent: boolean }) => {
  const handleLinkClick = React.useCallback(async () => {
    await report(OverviewTrackingEvents.WARTELISTE.LINK_CLICKED, isEntitledPlusRent);
  }, [isEntitledPlusRent]);
  const linkUrl = isEntitledPlusRent
    ? APP_CONFIG.baseApiUrl
    : '/meinkonto/premium-mitgliedschaft/?cmp_id=10-052961&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=waitinglist_cta';

  return (
    <div className='align-center'>
      <a className='button-secondary button-rounded' href={linkUrl} onClick={handleLinkClick}>
        {isEntitledPlusRent ? (
          <Msg id='benefits.warteliste.modal.linkButtonPlusCta' defaultMessage='Jetzt Suchkriterien anpassen' />
        ) : (
          <Msg id='benefits.warteliste.modal.linkButton' defaultMessage='Suchen+ entdecken' />
        )}
      </a>
    </div>
  );
};

export const Warteliste = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const isEntitledPlusRent = useGetHasProduct(ProductType.MieterPlus);

  return (
    <Modal
      title={t({
        id: 'benefits.warteliste.modal.headline',
        defaultMessage: 'Warteliste',
      })}
      isVisible={isVisible}
      image={WartelistePng}
      imageWebp={WartelisteWebp}
      imageMobile={WartelisteMobilePng}
      imageMobileWebp={WartelisteMobileWebp}
      imageAlt={t({
        id: 'benefits.warteliste.modal.image',
        defaultMessage: 'Warteliste',
      })}
      imageUseFullWidth={true}
      tabsContent={[<OverviewTab key='benefits.warteliste.modal.overview' isEntitledPlusRent={isEntitledPlusRent} />]}
      buttonArea={<ButtonArea isEntitledPlusRent={isEntitledPlusRent} />}
      benefit={Benefit.WARTELISTE}
      onClose={onClose}
    />
  );
};
